"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuthStore } from "@/store/auth";
import { formatPhoneForDisplay } from "@/lib/utils/phone";
import { usePortalFeatures } from "@/lib/portalFeatures";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Loader2,
  ArrowLeft,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
} from "lucide-react";

import {
  type AuthFormAppearance,
  loginFormLabelClass,
  loginInputClass,
  loginSubmitClass,
} from "./loginAppearance";
import { cn } from "@/lib/utils";

interface AuthRegisterFormProps {
  phone: string;
  onSuccess: () => void;
  onBack: () => void;
  onUserExists?: () => void;
  /** When "checkout", uses checkout copy and includes associationId in register payload */
  showBackButton?: boolean;
  appearance?: AuthFormAppearance;
}

export function AuthRegisterForm({
  phone,
  onSuccess,
  onBack,
  onUserExists,
  showBackButton = true,
  appearance = "default",
}: AuthRegisterFormProps) {
  const t = useTranslations();
  const { register: registerUser, isLoading } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const portalFeatures = usePortalFeatures();
  const showEmailMobile = portalFeatures.registerEmailMobileShow;
  const nameRequired = portalFeatures.nameRequired;

  const formSchema = z
    .object({
      name: nameRequired
        ? z.string().min(1, t("auth.nameRequired"))
        : z.string().optional(),
      email: z
        .string()
        .optional()
        .refine((v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), {
          message: t("auth.invalidEmail"),
        }),
      password: z
        .string()
        .min(1, t("auth.passwordRequired"))
        .min(6, t("auth.passwordMinLength")),
      confirmPassword: z.string().min(1, t("auth.confirmPasswordRequired")),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("auth.passwordMismatch"),
      path: ["confirmPassword"],
    });

  type RegisterFormValues = z.infer<typeof formSchema>;

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    mode: "onTouched",
  });

  const password = form.watch("password");
  const confirmPassword = form.watch("confirmPassword");
  const passwordsMatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    password === confirmPassword;
  const passwordsDontMatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    password !== confirmPassword;

  const onSubmit = form.handleSubmit(async (values) => {
    const result = await registerUser({
      mobile: phone,
      password: values.password,
      confirmPassword: values.confirmPassword,
      name: values.name || undefined,
      email: values.email || undefined,
    });

    if (result.success) {
      onSuccess();
      return;
    }

    const errorMsg = (result.message || "").toLowerCase();
    if (
      errorMsg.includes("already exists") ||
      errorMsg.includes("already registered") ||
      errorMsg.includes("مسجل مسبقاً") ||
      errorMsg.includes("موجود")
    ) {
      if (onUserExists) {
        onUserExists();
        return;
      }
    }
    form.setError("root", {
      message: result.message || t("auth.registerError"),
    });
  });

  const displayPhone = formatPhoneForDisplay(phone);

  return (
    <div className="space-y-4">
      {showBackButton && (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
          {t("common.back")}
        </button>
      )}

      <div className="p-3 bg-primary/5 text-center flex items-center justify-center gap-2 rounded-lg">
        <p className="text-sm opacity-90">{t("auth.createAccountWith")}</p>
        <p className="font-bold text-primary text-base" dir="ltr">
          {displayPhone}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={loginFormLabelClass(appearance)}>
                  {t("auth.name")}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("auth.name")}
                    className={cn("h-12", loginInputClass(appearance))}
                    disabled={isLoading}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {showEmailMobile ? (
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={loginFormLabelClass(appearance)}>
                    {t("auth.email")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t("auth.email")}
                      className={cn(
                        "h-12 placeholder:text-end",
                        loginInputClass(appearance),
                      )}
                      dir="ltr"
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={loginFormLabelClass(appearance)}>
                  {t("auth.password")}
                </FormLabel>
                <FormControl>
                  <div className="relative !py-0">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className={cn("h-12 pe-10", loginInputClass(appearance))}
                      disabled={isLoading}
                      {...field}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={loginFormLabelClass(appearance)}>
                  {t("auth.confirmPassword")}
                </FormLabel>
                <FormControl>
                  <div className="relative !py-0">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className={cn(
                        "h-12 pe-10 transition-colors",
                        loginInputClass(appearance),
                        passwordsMatch && "border-green-500 bg-green-50",
                        passwordsDontMatch && "border-red-500 bg-red-50",
                      )}
                      disabled={isLoading}
                      {...field}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </FormControl>
                {passwordsMatch && (
                  <p className="text-sm text-green-600 flex items-center gap-1 font-medium">
                    <CheckCircle2 className="h-4 w-4" />
                    {t("auth.passwordsMatch")}
                  </p>
                )}
                {passwordsDontMatch && (
                  <p className="text-sm text-red-500 flex items-center gap-1 font-medium">
                    <XCircle className="h-4 w-4" />
                    {t("auth.passwordMismatch")}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          {form.formState.errors.root && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
              <XCircle className="h-5 w-5 flex-shrink-0" />
              <span>{form.formState.errors.root.message}</span>
            </div>
          )}

          <Button
            type="submit"
            className={cn(
              appearance === "authPage"
                ? loginSubmitClass(appearance)
                : "w-full h-14 rounded-xl font-bold text-lg",
            )}
            disabled={isLoading || passwordsDontMatch}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin me-2" />
                {t("common.loading")}
              </>
            ) : (
              t("auth.register")
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}
